/**
 * Bundle 78 Task 2 - Public Law Enforcement Protocols page.
 *
 * Renders the markdown at `/api/legal/law-enforcement` so ops can
 * update the copy without a code deploy. Key Stripe-compliance
 * keywords ("law enforcement", "subpoena", "legal process",
 * "records request", "preservation request", "emergency disclosure",
 * "search warrant") are ALSO baked into the initial HTML fallback
 * block below so a crawler that skips JavaScript still finds them.
 *
 * No em/en dashes, no eslint-disable, no "photo"/"image" in alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { Scale } from 'lucide-react';

const TOC_SECTIONS = [
  { id: 'scope', label: '1. Scope' },
  { id: 'accepted-legal-process', label: '2. Accepted Legal Process' },
  { id: 'submit', label: '3. How to Submit a Request' },
  { id: 'produce', label: '4. What We Will and Will Not Produce' },
  { id: 'emergency', label: '5. Emergency Disclosure Requests' },
  { id: 'user-notice', label: '6. User Notice' },
  { id: 'fees', label: '7. Fees and Reimbursement' },
  { id: 'retention', label: '8. Records Retention' },
  { id: 'reporting', label: '9. Reporting Prohibited Conduct' },
  { id: 'anti-trafficking', label: '10. Anti-Trafficking Commitment' },
  { id: 'contact', label: '11. Contact' },
];

const H_TO_ID = {
  '1. Scope': 'scope',
  '2. Accepted Legal Process': 'accepted-legal-process',
  '3. How to Submit a Request': 'submit',
  '4. What Dostination Will and Will Not Produce': 'produce',
  '5. Emergency Disclosure Requests': 'emergency',
  '6. User Notice': 'user-notice',
  '7. Fees and Reimbursement': 'fees',
  '8. Records Retention': 'retention',
  '9. Reporting Prohibited Conduct': 'reporting',
  '10. Anti-Trafficking, Anti-Abuse Commitment': 'anti-trafficking',
  '11. Contact': 'contact',
};

function RenderMarkdown({ md }) {
  const blocks = useMemo(() => {
    if (!md) return [];
    const lines = md.split('\n');
    const out = [];
    let paragraph = [];
    let list = [];
    const flushParagraph = () => {
      if (paragraph.length) {
        out.push({ type: 'p', text: paragraph.join(' ').trim() });
        paragraph = [];
      }
    };
    const flushList = () => {
      if (list.length) {
        out.push({ type: 'ul', items: list.slice() });
        list = [];
      }
    };
    for (const raw of lines) {
      const line = raw.replace(/\r$/, '');
      if (!line.trim()) { flushParagraph(); flushList(); continue; }
      if (line.startsWith('# ')) { flushParagraph(); flushList(); out.push({ type: 'h1', text: line.slice(2) }); continue; }
      if (line.startsWith('## ')) {
        flushParagraph(); flushList();
        const label = line.slice(3).trim();
        const id = H_TO_ID[label] || label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        out.push({ type: 'h2', text: label, id });
        continue;
      }
      if (line.startsWith('- ')) {
        flushParagraph();
        list.push(line.slice(2));
        continue;
      }
      paragraph.push(line);
    }
    flushParagraph();
    flushList();
    return out;
  }, [md]);

  const inline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) {
        return <strong key={i} className="text-primary-900">{p.slice(2, -2)}</strong>;
      }
      const linkParts = [];
      let last = 0;
      const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
      let m;
      while ((m = linkRe.exec(p)) !== null) {
        if (m.index > last) linkParts.push(p.slice(last, m.index));
        linkParts.push(<a key={`l${i}-${m.index}`} href={m[2]} className="text-secondary-700 underline hover:text-secondary-800">{m[1]}</a>);
        last = m.index + m[0].length;
      }
      if (last < p.length) linkParts.push(p.slice(last));
      return <React.Fragment key={i}>{linkParts}</React.Fragment>;
    });
  };

  return (
    <div className="text-primary-800 leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h1 key={i} className="font-display text-3xl sm:text-4xl font-bold text-primary-900 mt-2 mb-4">{b.text}</h1>;
        if (b.type === 'h2') return (
          <h2 key={i} id={b.id} className="font-display text-lg font-semibold text-primary-900 mt-10 mb-3 scroll-mt-24" data-testid={`law-h-${b.id}`}>{b.text}</h2>
        );
        if (b.type === 'ul') return (
          <ul key={i} className="list-disc pl-6 my-3 space-y-1.5 text-primary-700">
            {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
          </ul>
        );
        return <p key={i} className="my-3 text-primary-700">{inline(b.text)}</p>;
      })}
    </div>
  );
}

const LawEnforcementPage = () => {
  const [md, setMd] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/legal/law-enforcement')
      .then((r) => {
        if (!mounted) return;
        setMd(r.data?.markdown || '');
        setUpdatedAt(r.data?.updated_at || null);
      })
      .catch(() => {
        if (mounted) setMd('');
      });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="law-enforcement-page">
      {/* Crawler-visible fallback: the Stripe compliance keywords must
          be present in the initial HTML even when JS is disabled. */}
      <div className="sr-only" aria-hidden="false" data-testid="law-compliance-fallback">
        Dostination cooperates with law enforcement pursuant to valid legal process, including
        subpoena, court order, and search warrant. We accept preservation requests and emergency
        disclosure requests, and we respond to records requests from government agencies and
        counsel. See sections 2 through 5 below for the full protocol.
      </div>

      <div className="border-b border-neutral-100 bg-neutral-50">
        <div className="container mx-auto px-4 md:px-8 py-10">
          <div className="flex items-center gap-3 text-secondary-800 text-xs uppercase tracking-widest font-medium">
            <Scale className="h-4 w-4" /> Law enforcement protocols
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-900 mt-3">Law Enforcement Protocols</h1>
          <p className="text-primary-700 mt-3 max-w-3xl">
            How to submit a records request, subpoena, court order, search warrant, preservation
            request, or emergency disclosure request to Dostination, and how we respond.
          </p>
          {updatedAt ? (
            <p className="text-xs text-neutral-500 mt-2" data-testid="law-updated-at">
              Last updated {new Date(updatedAt).toLocaleDateString()}
            </p>
          ) : null}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-10 grid gap-10 md:grid-cols-[240px_1fr]">
        <aside className="hidden md:block">
          <div className="sticky top-24">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">On this page</div>
            <ul className="space-y-1.5 text-sm">
              {TOC_SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-primary-700 hover:text-primary-900" data-testid={`law-toc-${s.id}`}>
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="max-w-3xl">
          {md ? <RenderMarkdown md={md} /> : (
            <div className="text-neutral-500 text-sm">Loading policy...</div>
          )}
          <p className="text-[11px] text-neutral-500 mt-10 border-t border-neutral-100 pt-4" data-testid="law-legal-notice">
            This document describes Dostination's process for responding to law enforcement
            requests. It is not legal advice.
          </p>
        </article>
      </div>
    </div>
  );
};

export default LawEnforcementPage;
